import { GetServerSideProps, GetServerSidePropsContext, PreviewData } from 'next/types';
import Head from 'next/head';
import { ParsedUrlQuery } from 'querystring';

import { metrics } from '@/common';
import { getRequestContext } from '@/utils/requestContext';
import { getGlobalHeaderServerSideProps } from '@eventbrite/consumer-header.global-header/server';
import { getGlobalFooterServerSideProps } from '@eventbrite/legacy-infra.site-footer/server';
import { useIntl } from 'react-intl';
import { EventbriteHeader, stageFromString, Stage } from '@eventbrite/webapp-constants';
import { OpenGraphTags, TwitterTags } from '@eventbrite/webapp-public-content';
import { GlobalHeaderProps } from '@eventbrite/consumer-header.global-header';
import { getHreflangLinks, isDualLanguageTLD } from './localeConfig';
import { loadI18nProps } from '@/utils/i18n';
import { createDefaultLocaleInfo } from '@/utils/localeInfo';
import { getGuides } from '@/api/guides/temp/getGuides';
import { GUIDE_MARKETS_BY_LOCATION_SLUG } from '@/api/guides/temp/hydrateGuideLocation';
import type { Guides } from '@/types/guides';
import { getUserLocationFromRequest } from '@/common';

const ROKT_CONFIG_TIMEOUT_MS = 1000;

async function getRoktApiKey(
    ebDomain: string,
    context: GetServerSidePropsContext,
): Promise<string> {
    const subdomain = process.env.NODE_ENV === 'development' ? 'www' : 'internal-api';
    const { headers } = context.req;
    const cookie = headers.cookie || '';
    const csrfHeader = headers['x-csrftoken'];
    const csrfToken =
        (Array.isArray(csrfHeader) ? csrfHeader[0] : csrfHeader) ||
        cookie.match(/csrftoken=([^;]+)/)?.[1];

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ROKT_CONFIG_TIMEOUT_MS);

    try {
        const response = await fetch(`https://${subdomain}.${ebDomain}/api/v3/system/rokt/`, {
            headers: {
                'content-type': 'application/json',
                cookie,
                Referer: `https://${subdomain}.${ebDomain}`,
                'x-csrftoken': csrfToken || '',
            },
            signal: controller.signal,
        });
        if (!response.ok) {
            return '';
        }

        const data: unknown = await response.json();
        return typeof (data as { api_key?: unknown })?.api_key === 'string'
            ? (data as { api_key: string }).api_key
            : '';
    } catch {
        return '';
    } finally {
        clearTimeout(timeoutId);
    }
}

const _getRequestId = (context: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>) =>
    (context.req?.headers[EventbriteHeader.AMZN_REQUESTID] as string) || 'MISSING_REQUEST_ID';

const handleRejectedResult = (result: PromiseSettledResult<unknown>, operationName: string) => {
    if (result.status === 'rejected') {
        console.error(`Failed to ${operationName}:`, result.reason);
    }
};

interface HomepageHeadProps {
    serverUrl: string;
    stage: Stage;
}

/**
 * Shared Head component for homepage with hreflang tags
 */
export function HomepageHead({ serverUrl, stage }: HomepageHeadProps) {
    const intl = useIntl();
    const pageTitle = intl.formatMessage({
        id: 'Eventbrite - Discover the Best Local Events & Things to Do',
        defaultMessage: 'Eventbrite - Discover the Best Local Events & Things to Do',
    });
    const pageDescription = intl.formatMessage({
        id: 'Find tickets to your next unforgettable experience. Browse concerts, workshops, yoga classes, charity events, food and music festivals, and more things to do.',
        defaultMessage:
            'Find tickets to your next unforgettable experience. Browse concerts, workshops, yoga classes, charity events, food and music festivals, and more things to do.',
    });

    const hreflangLinks = getHreflangLinks(stage);

    return (
        <Head>
            <title>{String(pageTitle)}</title>
            <link rel="canonical" href={serverUrl} />
            {hreflangLinks.map((link, index) => (
                <link key={index} rel="alternate" href={link.href} hrefLang={link.hreflang} />
            ))}
            <OpenGraphTags url={serverUrl} description={String(pageDescription)} />
            <TwitterTags description={String(pageTitle)} />
        </Head>
    );
}

/**
 * Shared getServerSideProps logic for homepage routes
 * @param localePathParam - Optional locale from URL path (e.g., 'fr_CA')
 */
export function getHomepageServerSideProps(localePathParam?: string): GetServerSideProps {
    return async (context: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>) => {
        const baseTags = {
            requestId: _getRequestId(context),
        };
        const publicUserId = (context.req?.headers[EventbriteHeader.EB_USER_ID] as string) || '';
        const isUserAuthenticated = !!publicUserId;
        const stage = stageFromString(process.env.STAGE || Stage.TEST);
        const { serverUrl, tld, host, locale } = getRequestContext(context.req);
        const ebDomain = host.replace(/^www\./, '');

        const effectiveLocale = localePathParam || locale || '';
        const localeInfo = createDefaultLocaleInfo(context.req);
        const i18nProps = await loadI18nProps(effectiveLocale);
        const [[globalHeaderResult, globalFooterResult], roktApiKey] = await Promise.all([
            Promise.allSettled([
                metrics.measure(
                    () => getGlobalHeaderServerSideProps(context),
                    'getGlobalHeaderServerSideProps',
                    { ...baseTags, loggedIn: String(isUserAuthenticated) },
                ),
                metrics.measure(
                    () => getGlobalFooterServerSideProps(context),
                    'getGlobalFooterServerSideProps',
                    { ...baseTags, loggedIn: String(isUserAuthenticated) },
                ),
            ]),
            getRoktApiKey(ebDomain, context),
        ]);

        handleRejectedResult(globalHeaderResult, 'fetch global header');
        handleRejectedResult(globalFooterResult, 'fetch global footer');

        const emptyGlobalHeader: GlobalHeaderProps = {
            env: {
                serverUrl: serverUrl,
            },
            request: {
                path: context.req.url,
                session_id: context.req.headers[EventbriteHeader.EB_SESSION_ID] as string,
            },
            isHomePage:
                context.req.url == '' ||
                context.req.url == '/' ||
                context.req.url?.startsWith('/home'),
        };

        const emptyGlobalFooter = {
            thirdFooterColumnName: 'Find Events',
            thirdFooterColumn: [],
            popularSearches: [],
            eventsToPlan: [],
            showMinimalLinks: false,
            impressumHelpLink: '',
        };

        const globalHeaderProps =
            globalHeaderResult.status === 'fulfilled'
                ? globalHeaderResult.value
                : emptyGlobalHeader;

        const globalFooterProps =
            globalFooterResult.status === 'fulfilled'
                ? {
                      ...globalFooterResult.value,
                      thirdFooterColumn: globalFooterResult.value.thirdFooterColumn ?? [],
                      eventsToPlan: globalFooterResult.value.eventsToPlan ?? [],
                      popularSearches: globalFooterResult.value.popularSearches ?? [],
                  }
                : emptyGlobalFooter;

        const footerProps = {
            serverUrl: serverUrl,
            localeInfo: localeInfo,
            isUserAuthenticated: isUserAuthenticated,
            globalFooterProps,
        };

        // Check if eb-location-slug header is present and in the guides dictionary
        const locationSlug = context.req?.headers['eb-location-slug'] as string | undefined;
        let guides: Guides | undefined;

        if (locationSlug && GUIDE_MARKETS_BY_LOCATION_SLUG[locationSlug]) {
            try {
                console.debug('Loading guides for location slug:', locationSlug);
                guides = await metrics.measure(
                    () => getGuides(context.req, { slug: locationSlug }),
                    'getGuides',
                    { ...baseTags, locationSlug },
                );
                console.debug('Guides loaded successfully:', {
                    locationSlug,
                    guidesCount: guides?.items?.length || 0,
                });
            } catch (error) {
                console.error('Error loading guides for location slug:', locationSlug, error);
                // Continue without guides if there's an error
            }
        }

        // For dual-language TLDs, remove browser cache (max-age=0) while keeping CloudFront cache
        // For other TLDs, keep standard caching behavior
        // this is to avoid the browser caching the wrong locale for dual-language TLDs
        const CACHE_CONTROL = isDualLanguageTLD(tld)
            ? 'max-age=0, s-maxage=600, stale-while-revalidate=3600, stale-if-error=86400'
            : 'max-age=300, s-maxage=600, stale-while-revalidate=3600, stale-if-error=86400';
        const { res } = context;
        res.setHeader('Cache-Control', CACHE_CONTROL);

        // Fetch user location on the server so it's available before first paint.
        // Normalize to null so getServerSideProps return value is JSON-serializable (no undefined).
        let location: Record<string, string | number | boolean | null> | undefined;
        try {
            const place = await metrics.measure(
                () => getUserLocationFromRequest(context.req),
                'getUserLocationFromRequest',
                baseTags,
            );
            if (place) {
                location = {
                    slug: place.slug ?? null,
                    placeId: place.placeId ?? null,
                    latitude: place.latitude ?? null,
                    longitude: place.longitude ?? null,
                    currentPlace: place.currentPlace ?? null,
                    currentPlaceParent: place.currentPlaceParent ?? null,
                    placeType: place.placeType ?? null,
                    isOnline: place.isOnline ?? null,
                };
            }
        } catch (error) {
            console.error('Error fetching user location in getServerSideProps:', error);
        }

        return {
            props: {
                stage,
                publicUserId,
                env: {
                    serverUrl,
                    tld,
                    ebDomain,
                    localeInfo,
                },
                globalHeaderProps,
                footerProps,
                i18nProps,
                isHomepageRoute: true,
                roktApiKey,
                ...(location && { location }),
                ...(guides && { guides }),
            },
        };
    };
}
